import { DM_REPLY_WINDOW_MS, type QueueItemDetail, type Role } from '@rp/contracts'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState, type ReactNode } from 'react'
import { isApiError } from '../../api/client'
import { Button, Card, DeadlineToken, EmptyState, Spinner } from '../../design'
import { replyCountdown, type Countdown } from '../../lib/time'
import { canReclassify } from '../../session/roles'
import { useReadySession } from '../../session/useSession'
import { useWideWorkspace } from '../../shell/useWideWorkspace'
import { KIND_LABELS, pageProblemOf } from '../queue/labels'
import { useQueueItem } from '../queue/queries'
import { ReclassifyDialog } from '../reclassify/ReclassifyDialog'
import { canEditDraft, replyAccess } from '../reply/access'
import { ReplyComposer } from '../reply/ReplyComposer'
import { useReplyDraft } from '../reply/useReplyDraft'
import { SuggestionPanel } from '../suggestion/SuggestionPanel'
import { ClaimBar, claimState } from './ClaimBar'
import { ClassificationPanel } from './ClassificationPanel'
import { useClaimItem, useReleaseItem } from './claimMutations'
import { ITEM_COPY } from './copy'
import { itemRefetchInterval } from './polling'
import { PostContext } from './PostContext'
import { ThreadView } from './ThreadView'
import { useViewHeartbeat } from './useViewHeartbeat'

const backToQueue = { to: '/', search: { folder: 'inbox' as const, page: 1 } } as const
const NOW_REFRESH_MS = 60_000

const useNow = (): Date => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), NOW_REFRESH_MS)
    return () => clearInterval(id)
  }, [])
  return now
}

const remainingShare = (deadlineAt: string | null, now: Date): number =>
  deadlineAt === null ? 0 : (Date.parse(deadlineAt) - now.getTime()) / DM_REPLY_WINDOW_MS

const LoadFailure = ({ error, onBack }: { error: unknown; onBack: () => void }) => {
  const notFound = isApiError(error) && error.code === 'NOT_FOUND'
  return (
    <EmptyState
      title={notFound ? ITEM_COPY.notFoundTitle : ITEM_COPY.loadFailed}
      description={notFound ? ITEM_COPY.notFoundBody : isApiError(error) ? error.message : ''}
      action={
        <Button variant="primary" onClick={onBack}>
          {ITEM_COPY.back}
        </Button>
      }
    />
  )
}

const PageWarning = ({ item }: { item: QueueItemDetail }) => {
  const problem = pageProblemOf(item.pageConnection)
  if (problem === null) return null
  return (
    <p className="item-page-warning tone-danger" role="alert">
      {problem}
    </p>
  )
}

type ItemHeadProps = {
  item: QueueItemDetail
  countdown: Countdown | null
  now: Date
  inPane: boolean
  action: ReactNode
}

const ItemHead = ({ item, countdown, now, inPane, action }: ItemHeadProps) => {
  const Title = inPane ? 'h2' : 'h1'
  return (
    <>
      <header className="item-head">
        <div className="item-identity">
          <Title className="item-title">{item.authorName}</Title>
          <p className="item-origin">{ITEM_COPY.origin(item.channelName, KIND_LABELS[item.kind])}</p>
        </div>
        {action}
      </header>
      <PageWarning item={item} />
      {countdown === null ? null : (
        <div className="item-deadline">
          <DeadlineToken label={countdown.text} remaining={remainingShare(item.replyDeadlineAt, now)} tone={countdown.tone} />
          <span className="item-deadline-note">{ITEM_COPY.deadline}</span>
        </div>
      )}
    </>
  )
}

type ItemAsideProps = {
  item: QueueItemDetail
  role: Role
  canAdopt: boolean
  onAdopt: (text: string) => void
}

const ItemAside = ({ item, role, canAdopt, onAdopt }: ItemAsideProps) => {
  const [reclassifying, setReclassifying] = useState(false)
  return (
    <div className="item-aside">
      <ClassificationPanel
        classification={item.classification}
        canReclassify={canReclassify(role)}
        onReclassify={() => setReclassifying(true)}
      />
      <SuggestionPanel item={item} canAdopt={canAdopt} onAdopt={onAdopt} />
      <ReclassifyDialog item={item} open={reclassifying} onClose={() => setReclassifying(false)} />
    </div>
  )
}

const ItemWorkspace = ({ item, now, onBack }: { item: QueueItemDetail; now: Date; onBack: () => void }) => {
  const { me, membership } = useReadySession()
  const claim = useClaimItem(item.id)
  const release = useReleaseItem(item.id)
  const inPane = useWideWorkspace()
  const draft = useReplyDraft(item)
  useViewHeartbeat(item.id)
  const mutationError = [claim.error, release.error].find(isApiError)?.message ?? null
  const countdown = replyCountdown(item.replyDeadlineAt === null ? null : new Date(item.replyDeadlineAt), now)
  const access = replyAccess(item, claimState(item, me.user.id, now))

  return (
    <section className="item">
      <div className="item-main">
        <Button className="item-back" variant="ghost" onClick={onBack}>
          {ITEM_COPY.back}
        </Button>
        <ItemHead
          item={item}
          countdown={countdown}
          now={now}
          inPane={inPane}
          action={
            <ClaimBar
              item={item}
              currentUserId={me.user.id}
              role={membership.role}
              now={now}
              onClaim={(options) => claim.mutate(options)}
              onRelease={() => release.mutate()}
              pending={claim.isPending || release.isPending}
              error={mutationError}
            />
          }
        />
        {item.post === null ? null : (
          <Card className="post-card">
            <PostContext post={item.post} platform={item.platform} now={now} />
          </Card>
        )}
        <Card as="section" className="thread-card">
          <ThreadView
            platform={item.platform}
            messages={item.messages}
            now={now}
            olderMessageCount={item.olderMessageCount}
          />
        </Card>
      </div>
      <ItemAside
        item={item}
        role={membership.role}
        canAdopt={canEditDraft(membership.role, access)}
        onAdopt={draft.adoptSource}
      />
      <ReplyComposer item={item} access={access} draft={draft} deadlineExpired={countdown?.expired ?? false} />
    </section>
  )
}

export const ItemPage = () => {
  const { itemId } = useParams({ from: '/items/$itemId' })
  const navigate = useNavigate()
  const now = useNow()
  const query = useQueueItem(itemId, { refetchInterval: itemRefetchInterval })
  const goBack = () => void navigate(backToQueue)

  if (query.isPending) return <Spinner label={ITEM_COPY.loading} />
  if (query.isError) return <LoadFailure error={query.error} onBack={goBack} />
  return <ItemWorkspace key={query.data.id} item={query.data} now={now} onBack={goBack} />
}
